import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutoAwesomeOutlined as AssistantIcon,
  Close as CloseIcon,
  EditNoteOutlined as NewChatIcon,
  NorthEast as OpenIcon,
  ReceiptLongOutlined as FeedIcon,
  AttachFileRounded as AttachIcon,
  HistoryRounded as HistoryIcon,
  ThumbDownAltOutlined as ThumbDownIcon,
  ThumbUpAltOutlined as ThumbUpIcon,
  Search as LookupIcon,
  Send as SendIcon,
  StopCircleOutlined as StopIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../Context/AppContext";
import { apiGetRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { previewBadge } from "../../Layouts/CommunityLayout/styles/styles";
import useAgentChat, { AGENT_ATTACHMENT_TYPES, AGENT_MESSAGE_MAX_CHARS } from "./useAgentChat";
import AgentAttachment from "./AgentAttachment";
import AgentHistory from "./AgentHistory";
import AgentMessageText from "./AgentMessageText";
import AgentActionCard from "./AgentActionCard";
import AgentActivityFeed from "./AgentActivityFeed";
import AgentThinking from "./AgentThinking";
import { agentChatStyles as styles } from "./styles";
import { setAgentRole, trackAgent } from "./analytics";

// The admin app's chat assistant. Suppliers: COMY1-390 look-ups; COMY1-391
// changes behind Confirm cards, Undo and the "What I did" feed; COMY1-392
// pricelists typed or from a photo, products with photos, saved chats.
// Community managers and moderators: COMY1-393 look-ups over the community
// that is open; COMY1-394 changes behind Confirm cards (members, suppliers,
// settings), the community AI's drafts with Undo, photos (the community's
// picture, a question's screenshot) and switching community. Shown only when GET /api/agent/status says it's on for this
// person in their current role view (flagged suppliers, flagged communities).
// Everyone else sees nothing new.

const ROLES = ["supplier", "manager", "moderator"];
const SUGGESTIONS = {
  supplier: ["waiting", "inProgress", "prices"],
  community: ["joinRequests", "supplierOffers", "draftReply", "terms"],
};

export default function AgentChat() {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("agent");
  const { isCommunityManagementRole, profileData } = useContext(AppContext);
  const locale = i18n.language?.startsWith("en") ? "en" : "he";

  // the role view and (manager / moderator) community the server serves this person in
  const [{ role, communityId }, setServed] = useState({ role: null, communityId: null });
  useEffect(() => {
    let cancelled = false;
    apiGetRequest(endpoints.agentStatus)
      .then(({ data }) => {
        if (cancelled) return;
        const served = data?.enabled && ROLES.includes(data.role) ? data.role : null;
        setAgentRole(served);
        setServed({ role: served, communityId: served ? (data.communityId ?? null) : null });
      })
      .catch(() => !cancelled && setServed({ role: null, communityId: null }));
    return () => {
      cancelled = true;
    };
  }, [isCommunityManagementRole, profileData?.id]);

  const [open, setOpen] = useState(false);
  // the chat, the "What I did" feed, or the saved chats
  const [view, setView] = useState("chat");
  const [draft, setDraft] = useState("");
  // why a picked file wasn't taken (type, size, count, upload)
  const [fileError, setFileError] = useState(null);
  const {
    conversationId,
    messages,
    status,
    error,
    send,
    stop,
    reset,
    rate,
    decide,
    expire,
    loadFeed,
    pending,
    attach,
    detach,
    restoring,
    openConversation,
    loadConversations,
    deleteConversations,
    communitySwitched,
  } = useAgentChat({
    locale,
    // a manager's chats belong to the community they were in
    resetKey: `${profileData?.id}:${role}${communityId ? `:${communityId}` : ""}`,
  });
  const busy = status !== "idle";
  const uploading = pending.some((a) => a.uploading);
  const fileInputRef = useRef(null);

  // The chat switched community: once the reply is done, reload the app there, as the sidebar's switch
  // does (navigate(0)); the new community's chat starts fresh.
  useEffect(() => {
    if (!communitySwitched || busy) return undefined;
    const timer = setTimeout(() => navigate(0), 1500);
    return () => clearTimeout(timer);
  }, [communitySwitched, busy, navigate]);

  const listRef = useRef(null);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, status, error]);

  const inputRef = useRef(null);
  useEffect(() => {
    if (open && !busy) inputRef.current?.focus();
  }, [open, busy]);

  const submit = useCallback(
    (text) => {
      if ((!text.trim() && !pending.some((a) => a.id)) || busy || uploading || restoring) return;
      send(text);
      setDraft("");
      setFileError(null);
    },
    [busy, pending, restoring, send, uploading],
  );

  const pickFiles = async (fileList) => {
    setFileError(null);
    for (const file of Array.from(fileList ?? [])) {
      const { error: reason } = await attach(file);
      if (reason) setFileError(reason);
    }
  };

  const toggleView = (next) => {
    if (view !== next && next === "feed") trackAgent("feed_opened");
    if (view !== next && next === "history") trackAgent("history_opened");
    setView(view === next ? "chat" : next);
  };

  // Edit: the card is dropped and the person says in the chat what to change
  const editInChat = useCallback(() => inputRef.current?.focus(), []);

  const openScreen = (link) => {
    if (typeof link !== "string" || !link) return;
    // the screen only — never the job id in the query
    trackAgent("handoff_clicked", { screen: link.split("?")[0] });
    navigate(link);
    if (isBelowMd) setOpen(false);
  };

  if (!role) return null;

  // the community roles have their own wording and suggestions; every role can attach photos (S5)
  const isSupplier = role === "supplier";
  const copy = (key) => (isSupplier ? t(key) : t(`community.${key}`));

  const errorText =
    error?.kind === "notEnabled"
      ? t("notEnabled")
      : error?.kind === "invalid"
        ? t("invalid")
        : error && t("unavailable");

  return (
    <>
      {!open && (
        <Tooltip title={t("open")} placement="top">
          <Fab aria-label={t("open")} onClick={() => setOpen(true)} sx={styles.launcher(isBelowMd)}>
            <AssistantIcon />
          </Fab>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        variant={isBelowMd ? "temporary" : "persistent"}
        PaperProps={{ sx: styles.panel(isBelowMd), role: "dialog", "aria-label": t("title") }}
      >
        <Box sx={styles.header}>
          <AssistantIcon sx={styles.headerIcon} />
          <Typography sx={styles.title}>{t("title")}</Typography>
          <Box component="span" sx={previewBadge}>
            {t("badge")}
          </Box>
          <Box sx={{ marginInlineStart: "auto", display: "flex", gap: "4px" }}>
            <Tooltip title={t("history.title")}>
              <IconButton
                size="small"
                aria-label={t("history.title")}
                aria-pressed={view === "history"}
                onClick={() => toggleView("history")}
                sx={styles.viewToggle(view === "history")}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("feed.title")}>
              <IconButton
                size="small"
                aria-label={t("feed.title")}
                aria-pressed={view === "feed"}
                onClick={() => toggleView("feed")}
                sx={styles.viewToggle(view === "feed")}
              >
                <FeedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("newChat")}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t("newChat")}
                  onClick={() => {
                    reset();
                    setView("chat");
                  }}
                  disabled={!messages.length && !error}
                >
                  <NewChatIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" aria-label={t("close")} onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box ref={listRef} sx={styles.list} className="custom-scrollbar" aria-live="polite">
          {view === "feed" && <AgentActivityFeed load={loadFeed} locale={locale} onOpen={openScreen} />}
          {view === "history" && (
            <AgentHistory
              load={loadConversations}
              locale={locale}
              currentId={messages.length ? conversationId : null}
              onOpen={async (id) => {
                trackAgent("history_reopened");
                await openConversation(id);
                setView("chat");
              }}
              onDelete={deleteConversations}
            />
          )}
          {view === "chat" && restoring && <CircularProgress size={20} sx={{ color: "#46A091", alignSelf: "center", mt: "24px" }} />}
          {view === "chat" && !restoring && !messages.length && (
            <Box sx={styles.empty}>
              <Typography sx={styles.greeting}>
                {profileData?.firstName ? t("greeting", { p: profileData.firstName }) : t("greetingNoName")}
              </Typography>
              <Typography sx={styles.intro}>{copy("intro")}</Typography>
              <Box sx={styles.suggestions}>
                {SUGGESTIONS[isSupplier ? "supplier" : "community"].map((key) => (
                  <Button key={key} variant="outlined" size="small" sx={styles.suggestion} onClick={() => submit(copy(`suggestions.${key}`))}>
                    {copy(`suggestions.${key}`)}
                  </Button>
                ))}
                {/* a pricelist photo starts with picking the photo */}
                {isSupplier && (
                  <Button variant="outlined" size="small" startIcon={<AttachIcon sx={{ transform: "rotate(45deg)" }} />} sx={styles.suggestion} onClick={() => fileInputRef.current?.click()}>
                    {t("suggestions.photo")}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {view === "chat" && messages.map((m, i) => {
            const isLast = i === messages.length - 1;
            if (m.role === "user") {
              return (
                <Box key={m.id} sx={styles.userRow}>
                  {!!m.attachments?.length && (
                    <Box sx={styles.attachments}>
                      {m.attachments.map((a) => (
                        <AgentAttachment key={a.id} attachment={a} conversationId={conversationId} />
                      ))}
                    </Box>
                  )}
                  {!!m.text && (
                    <Box sx={styles.userBubble} dir="auto">
                      {m.text}
                    </Box>
                  )}
                </Box>
              );
            }
            // a lookup the model is waiting on
            const lookingUp = isLast && busy && !!m.pendingTools?.length;
            // the agent is between words: before the first one, or after a lookup until its next sentence starts
            const thinking = isLast && busy && (status === "waiting" || lookingUp);
            return (
              <Box key={m.id} sx={styles.assistantRow} aria-busy={isLast && busy ? true : undefined}>
                {!!m.chips.length && (
                  <Box sx={styles.chips} aria-label={t("lookups")}>
                    {m.chips.map((chip, j) => (
                      <Chip key={j} size="small" variant="outlined" icon={<LookupIcon />} label={chip} sx={styles.chip} />
                    ))}
                  </Box>
                )}
                {!!m.text && (
                  <Box sx={styles.assistantBubble}>
                    <AgentMessageText text={m.text} />
                  </Box>
                )}
                {thinking && <AgentThinking phase={lookingUp ? "lookingUp" : "thinking"} />}
                {!thinking && !!m.text && !(isLast && busy) && (
                  <Box sx={styles.feedback}>
                    {[
                      ["up", ThumbUpIcon, t("helpful")],
                      ["down", ThumbDownIcon, t("notHelpful")],
                    ].map(([value, Icon, label]) => (
                      <Tooltip key={value} title={label}>
                        <IconButton
                          size="small"
                          aria-label={label}
                          aria-pressed={m.feedback === value}
                          onClick={() => rate(m.id, value)}
                          sx={styles.feedbackButton(m.feedback === value)}
                        >
                          <Icon sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                )}
                {(m.actions ?? []).map((a) => (
                  <AgentActionCard key={a.id} action={a} onDecide={decide} onExpire={expire} onEdit={editInChat} conversationId={conversationId} />
                ))}
                {!!m.buttons.length && (
                  <Box sx={styles.buttons}>
                    {m.buttons.map((b, j) => (
                      <Button key={j} variant="outlined" size="small" endIcon={<OpenIcon sx={styles.openIcon} />} sx={styles.handoff} onClick={() => openScreen(b.link)}>
                        {b.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}

          {view === "chat" && error && (
            <Box sx={styles.error} role="alert">
              <Typography sx={styles.errorText}>{errorText}</Typography>
              {error.kind === "unavailable" && (
                <Button size="small" sx={styles.retry} onClick={reset}>
                  {t("startOver")}
                </Button>
              )}
            </Box>
          )}
        </Box>

        {(pending.length > 0 || fileError) && (
          <Box sx={styles.pending}>
            {pending.map((a) => (
              <AgentAttachment key={a.localId} attachment={a} conversationId={conversationId} onRemove={detach} size={48} />
            ))}
            {fileError && (
              <Typography sx={styles.fileError} role="alert">
                {t(`attach.errors.${fileError}`)}
              </Typography>
            )}
            {pending.length > 0 && <Typography sx={styles.attachPrivacy}>{t("attach.privacy")}</Typography>}
          </Box>
        )}
        <Box
          component="form"
          sx={styles.composer}
          onSubmit={(e) => {
            e.preventDefault();
            submit(draft);
          }}
        >
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={AGENT_ATTACHMENT_TYPES.join(",")}
              onChange={(e) => {
                pickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Tooltip title={t("attach.button")}>
              <span>
                <IconButton
                  aria-label={t("attach.button")}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || error?.kind === "notEnabled"}
                  sx={styles.attachButton}
                >
                  <AttachIcon sx={{ transform: "rotate(45deg)" }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
          <TextField
            inputRef={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit(draft);
              }
            }}
            placeholder={copy("placeholder")}
            multiline
            maxRows={4}
            fullWidth
            size="small"
            disabled={error?.kind === "notEnabled"}
            inputProps={{ maxLength: AGENT_MESSAGE_MAX_CHARS, dir: "auto", "aria-label": copy("placeholder") }}
            sx={styles.input}
          />
          {busy ? (
            <IconButton aria-label={t("stop")} onClick={stop} sx={styles.sendButton}>
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              type="submit"
              aria-label={t("send")}
              disabled={(!draft.trim() && !pending.some((a) => a.id)) || uploading}
              sx={styles.sendButton}
            >
              <SendIcon sx={{ transform: theme.direction === "rtl" ? "scaleX(-1)" : "none" }} />
            </IconButton>
          )}
        </Box>
        <Typography sx={styles.disclaimer}>{t("disclaimer")}</Typography>
      </Drawer>
    </>
  );
}
